import React, { useState, useEffect } from 'react';
import {
	GridColDef,
	DataGrid,
	GridToolbar,
	GridRenderCellParams,
	GridCellParams,
} from '@mui/x-data-grid';
import { Chip, Tooltip, Box, Button } from '@mui/material';

import { APIService } from '../services/apiService';
import { CourseMappingHeadDTO } from '../DTO/CourseMappingHeadDTO';
import { appVerdictName, levelName, VerdictStatus } from '../util/enumerations';
import VerdictHistoryModal from './application/verdict/VerdictHistoryModal';
import { useAuth } from '../contexts';

interface CourseTagProps {
	course: {
		code: string;
		title: string;
		ectsCredits?: number;
		localCredits: number;
		level: number;
	};
}

const CourseTag: React.FC<CourseTagProps> = ({ course }) => {
	const tooltipContent = (
		<Box sx={{ p: 0.5 }}>
			<div>
				<strong>Title:</strong> {course.title}
			</div>
			<div>
				<strong>ECTS credits:</strong> {course.ectsCredits || 'N/A'}
			</div>
			<div>
				<strong>Local credits:</strong> {course.localCredits}
			</div>
			<div>
				<strong>Level:</strong> {levelName(course.level)}
			</div>
		</Box>
	);

	return (
		<Tooltip title={tooltipContent} arrow>
			<Chip
				label={course.code}
				size="small"
				variant="outlined"
				sx={{
					margin: 0.25,
					fontWeight: 'bold',
				}}
			/>
		</Tooltip>
	);
};

const getVerdictStatusClassName = (status: VerdictStatus): string => {
	switch (status) {
		case VerdictStatus.Accepted:
		case VerdictStatus.AcceptedUnderSpecialCircumstances:
			return 'verdict-accepted';
		case VerdictStatus.Rejected:
			return 'verdict-rejected';
		case VerdictStatus.Unhandled:
		default:
			return 'verdict-unhandled';
	}
};

const createColDefs = (
	handleOpenModal: (id: number) => void
): GridColDef<CourseMappingHeadDTO>[] => [
	{ field: 'id', headerName: 'ID', type: 'number', flex: 0.5, minWidth: 50 },
	{
		field: 'school',
		headerName: 'School',
		flex: 1.5,
		minWidth: 150,
		renderCell: (params: GridRenderCellParams<CourseMappingHeadDTO>) => (
			<span>{params.row.school.name}</span>
		),
	},
	{
		field: 'homeCourses',
		headerName: 'Home Courses',
		flex: 2,
		minWidth: 200,
		renderCell: (params: GridRenderCellParams<CourseMappingHeadDTO>) => (
			<Box
				sx={{
					display: 'flex',
					flexWrap: 'wrap',
					gap: 0.5,
					justifyContent: 'center',
					alignItems: 'center',
					height: '100%',
				}}
			>
				{params.row.coursesHome.map((course, index) => (
					<CourseTag
						key={`${course.code}-${index}`}
						course={course}
					/>
				))}
			</Box>
		),
	},
	{
		field: 'exchangeCourses',
		headerName: 'Exchange Courses',
		flex: 2,
		minWidth: 200,
		renderCell: (params: GridRenderCellParams<CourseMappingHeadDTO>) => (
			<Box
				sx={{
					display: 'flex',
					flexWrap: 'wrap',
					gap: 0.5,
					justifyContent: 'center',
					alignItems: 'center',
					height: '100%',
				}}
			>
				{params.row.coursesExchange.map((course, index) => (
					<CourseTag
						key={`${course.code}-${index}`}
						course={course}
					/>
				))}
			</Box>
		),
	},
	{
		field: 'year',
		headerName: 'Year',
		flex: 0.8,
		minWidth: 80,
		renderCell: (params: GridRenderCellParams<CourseMappingHeadDTO>) => (
			<span>{params.row.year}</span>
		),
	},
	{
		field: 'latestVerdictStatus',
		headerName: 'Latest verdict',
		type: 'string',
		flex: 1,
		minWidth: 120,
		cellClassName: (params: GridCellParams<CourseMappingHeadDTO>) =>
			getVerdictStatusClassName(params?.row?.latestVerdictStatus),
		valueGetter: (params: number) => appVerdictName(params),
	},
	{
		field: 'verdicts',
		headerName: 'Verdicts',
		type: 'number',
		flex: 0.8,
		minWidth: 100,
		renderCell: (params: GridRenderCellParams<CourseMappingHeadDTO>) => {
			const verdictCount = params.row.verdicts?.length || 0;
			return (
				<Button
					variant="text"
					color="primary"
					onClick={() => handleOpenModal(params.row.id)}
				>
					{verdictCount}
				</Button>
			);
		},
	},
];

interface CourseMappingsGridProps {
	schoolId?: number;
	exchangeCourse?: string;
	homeCourse?: string;
	year?: number;
	courseIds?: number[];
	height?: string;
	minimalistic?: boolean;
}

const CourseMappingsGrid: React.FC<CourseMappingsGridProps> = ({
	schoolId,
	exchangeCourse,
	homeCourse,
	year,
	courseIds,
	height = 'calc(100vh - 350px)',
	minimalistic = false,
}) => {
	const [mappings, setMappings] = useState<CourseMappingHeadDTO[]>([]);
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedMappingId, setSelectedMappingId] = useState<number | null>(
		null
	);
	const auth = useAuth();
	const api = new APIService();

	useEffect(() => {
		// Only load if at least one parameter is provided
		if (
			schoolId ||
			exchangeCourse ||
			homeCourse ||
			year ||
			(courseIds && courseIds.length > 0)
		) {
			setLoading(true);
			const debounceTimer = setTimeout(async () => {
				await loadMappings();
			}, 500);

			return () => clearTimeout(debounceTimer);
		}
	}, [exchangeCourse, homeCourse, year, schoolId, courseIds]);

	const loadMappings = async () => {
		setLoading(true);
		try {
			const mappingsData = await api.getCourseMappings(
				schoolId,
				exchangeCourse,
				homeCourse,
				year,
				courseIds
			);
			setMappings(mappingsData);
		} catch (error) {
			console.error('Failed to load course mappings:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleOpenModal = (mappingId: number) => {
		setSelectedMappingId(mappingId);
		setModalOpen(true);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setSelectedMappingId(null);
	};

	const colDefs = createColDefs(handleOpenModal);

	return (
		<div style={{ height, width: '100%' }}>
			<DataGrid
				getRowId={(row) => `${row.year}-${row.id}`}
				initialState={{
					columns: {
						columnVisibilityModel: {
							id: !minimalistic,
							school: !minimalistic,
							verdicts: !!auth.user?.isAdmin,
						},
					},
				}}
				columns={colDefs}
				rows={mappings}
				loading={loading}
				slots={minimalistic ? {} : { toolbar: GridToolbar }}
			/>
			{selectedMappingId && (
				<VerdictHistoryModal
					open={modalOpen}
					onClose={handleCloseModal}
					courseMappingHeadId={selectedMappingId}
				/>
			)}
		</div>
	);
};

export default CourseMappingsGrid;
